import { useQuery } from '@apollo/client';
import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import GET_CURRENT_TENANT from 'api/queries/GET_CURRENT_TENANT';
import Input from 'layout/atoms/Input/Input';
import Label from 'layout/atoms/Label';
import Modal from 'layout/atoms/Modal/Modal';
import FormField from 'layout/molecules/FormField/FormField';
import FormSaveActions from 'layout/molecules/FormSaveActions/FormSaveActions';
import Grid from 'layout/molecules/Grid/Grid';
import Select from 'layout/molecules/Select/Select';
import { closeModal, selectModalByName } from 'store/layoutSlice';
import { fetchElements, selectElements } from 'store/parameters/elementsSlice';
import { addTemplate, assignTemplateElement, selectTemplateById, updateTemplate, withdrawTemplateElement, } from 'store/parameters/templatesSlice';
import useTemplateValidation from './useTemplateValidation';
function TemplateEditor({ create = false }) {
    var _a, _b, _c;
    const dispatch = useDispatch();
    const templateId = (_a = useSelector(selectModalByName(create ? 'templateCreator' : 'templateEditor'))) === null || _a === void 0 ? void 0 : _a.id;
    const templateDetails = useSelector((state) => selectTemplateById(state, templateId || '')) || null;
    const elements = useSelector(selectElements);
    const { t } = useTranslation(['specification', 'common']);
    const { data: tenant } = useQuery(GET_CURRENT_TENANT);
    const tenantId = (_c = (_b = tenant === null || tenant === void 0 ? void 0 : tenant.me) === null || _b === void 0 ? void 0 : _b.currentTenant) === null || _c === void 0 ? void 0 : _c.id;
    const [formValues, setFormValues] = useState({
        name: '',
        elements: [],
    });
    const { errors, isValid, pendingFields, markTouched, validateAll } = useTemplateValidation(formValues, t);
    useEffect(() => {
        var _a;
        dispatch(fetchElements());
        if (!create && templateDetails) {
            setFormValues({
                name: templateDetails.name,
                elements: (((_a = templateDetails.templateElements) === null || _a === void 0 ? void 0 : _a.edges) || []).map((edge) => edge.node.element),
            });
        }
    }, [dispatch, create, templateDetails, templateId]);
    const handleFormChange = ({ target: { name, value }, }) => {
        setFormValues((values) => (Object.assign(Object.assign({}, values), { [name]: value })));
    };
    const handleElementsChange = (value) => {
        const ids = Array.isArray(value) ? value : [value];
        const selected = elements.filter((element) => ids.includes(element.id));
        setFormValues((values) => (Object.assign(Object.assign({}, values), { elements: selected })));
    };
    const handleCancel = () => {
        dispatch(closeModal({ name: create ? 'templateCreator' : 'templateEditor' }));
    };
    const handleFormSubmit = () => {
        var _a;
        if (!validateAll())
            return;
        if (create) {
            dispatch(addTemplate({
                tenantId,
                name: formValues.name,
                elementIds: formValues.elements.map((element) => element.id),
            }));
        }
        else {
            dispatch(updateTemplate({ templateId: templateDetails.id, name: formValues.name }));
            const assignedEdges = ((_a = templateDetails.templateElements) === null || _a === void 0 ? void 0 : _a.edges) || [];
            const originalElementIds = assignedEdges.map((edge) => edge.node.element.id);
            const newElementIds = formValues.elements.map((element) => element.id);
            newElementIds
                .filter((elementId) => !originalElementIds.includes(elementId))
                .forEach((elementId) => dispatch(assignTemplateElement({ templateId: templateDetails.id, elementId })));
            assignedEdges
                .filter((edge) => !newElementIds.includes(edge.node.element.id))
                .forEach((edge) => dispatch(withdrawTemplateElement({
                templateId: templateDetails.id,
                templateElementId: edge.node.id,
            })));
        }
        dispatch(closeModal({ name: create ? 'templateCreator' : 'templateEditor' }));
    };
    return (React.createElement(Modal, { name: create ? 'templateCreator' : 'templateEditor', size: "s", header: React.createElement(Label, { weight: "strong" }, create ? t('add_template') : t('edit_template')), footer: React.createElement(FormSaveActions, { isValid: isValid, isLoading: false, pendingFields: pendingFields, onSave: handleFormSubmit, onCancel: handleCancel, validateAll: validateAll }), onClose: handleCancel },
        React.createElement(Grid, null,
            React.createElement(FormField, { label: t('common:name'), error: errors.name },
                React.createElement(Input, { value: formValues === null || formValues === void 0 ? void 0 : formValues.name, onChange: handleFormChange, onBlur: () => markTouched('name'), name: "name", error: Boolean(errors.name), autoFocus: true })),
            React.createElement(FormField, { label: t('elements') },
                React.createElement(Select, { fullWidth: true, multiselect: true, theme: "tags", options: elements.map((element) => ({ value: element.id, label: element.name })), value: formValues.elements.map((element) => element.id), onChange: handleElementsChange, placeholder: t('elements') })))));
}
export default memo(TemplateEditor);
